import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../main/menu';
import './styles/AIPromptSettings.css';
import { auth, db, getEffectiveUserId } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection } from 'firebase/firestore';
import { useCustomerSwitch } from '../contexts/CustomerSwitchContext';
import { availableLanguages, defaultLanguage } from '../languages/prompts';
import { useLanguage } from '../contexts/LanguageContext';

const AIPromptSettings = ({ onLogout }) => {
  const { selectedCustomer, isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const { t } = useLanguage();
  const [linkedInProfiles, setLinkedInProfiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [examples, setExamples] = useState([]);
  const [newProfile, setNewProfile] = useState('');
  const [newProduct, setNewProduct] = useState({ name: '', description: '' });
  const [newLeadMagnet, setNewLeadMagnet] = useState({ name: '', description: '' });
  const [newExample, setNewExample] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [modalType, setModalType] = useState(null); // 'profile', 'product', 'leadMagnet', eller 'example'
  const [editingItem, setEditingItem] = useState(null);
  const [ctaFrequency, setCtaFrequency] = useState('always'); // 'always', 'never', 'every2', 'every3', 'every4'
  const [emojiFrequency, setEmojiFrequency] = useState('medium'); // 'none', 'few', 'medium', 'many'
  const [preferredEmojis, setPreferredEmojis] = useState(''); // Nyt state for brugerens foretrukne emojis
  const [postLanguage, setPostLanguage] = useState(defaultLanguage); // Nyt state for opslagssprog
  const navigate = useNavigate();
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [settings, setSettings] = useState({
    linkedInProfiles: [],
    products: [],
    leadMagnets: [],
    examples: [],
    ctaFrequency: 'always',
    emojiFrequency: 'medium',
    preferredEmojis: '',
    postLanguage: 'da',
    aboutMe: '',  // Tilføjet about-felt
    customPrompt: ''  // Tilføjet brugerdefineret prompt
  });

  // Tilføj local state og debouncing for aboutMe
  const [aboutMeInput, setAboutMeInput] = useState('');
  const aboutMeTimeoutRef = useRef(null);

  // Tilføj local state og debouncing for customPrompt
  const [customPromptInput, setCustomPromptInput] = useState('');
  const customPromptTimeoutRef = useRef(null);

  // Hent brugerens prompt-indstillinger når komponenten indlæses eller når selectedCustomer ændres
  useEffect(() => {
    const fetchSettings = async () => {
      if (!auth.currentUser) return;

      try {
        // Brug getEffectiveUserId for at få den korrekte bruger-ID baseret på om vi arbejder på vegne af en kunde
        const effectiveUserId = getEffectiveUserId(selectedCustomer);
        
        if (!effectiveUserId) {
          console.error("Ingen gyldig bruger-ID fundet");
          return;
        }
        
        console.log("Henter indstillinger for bruger:", effectiveUserId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
        
        const settingsRef = doc(db, 'users', effectiveUserId, 'settings', 'ai_prompts');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setLinkedInProfiles(data.linkedInProfiles || []);
          setProducts(data.products || []);
          setLeadMagnets(data.leadMagnets || []);
          setExamples(data.examples || []);
          
          // Hent CTA-frekvens indstillingen
          if (data.ctaFrequency) {
            setCtaFrequency(data.ctaFrequency);
          }
          
          // Hent emoji-frekvens indstillingen
          if (data.emojiFrequency) {
            setEmojiFrequency(data.emojiFrequency);
          }
          
          // Hent brugerens foretrukne emojis
          if (data.preferredEmojis) {
            setPreferredEmojis(data.preferredEmojis);
          }
          
          // Hent opslagssprog
          if (data.postLanguage) {
            setPostLanguage(data.postLanguage);
          }
          
          // Hent about-felt
          if (data.aboutMe) {
            setSettings(prev => ({ ...prev, aboutMe: data.aboutMe }));
            setAboutMeInput(data.aboutMe); // Synkroniser input state
          }
          
          // Hent brugerdefineret prompt
          if (data.customPrompt) {
            setSettings(prev => ({ ...prev, customPrompt: data.customPrompt }));
            setCustomPromptInput(data.customPrompt); // Synkroniser input state
          }
        } else {
          // Hvis der ikke findes indstillinger for kunden, nulstil state
          setLinkedInProfiles([]);
          setProducts([]);
          setLeadMagnets([]);
          setExamples([]);
          setCtaFrequency('always'); // Standard er altid at inkludere CTA
          setEmojiFrequency('medium'); // Standard er medium emoji-brug
          setPreferredEmojis(''); // Standard er ingen foretrukne emojis
          setPostLanguage(defaultLanguage); // Standard sprog (dansk)
          setSettings(prev => ({ ...prev, aboutMe: '', customPrompt: '' }));
          setAboutMeInput(''); // Reset input state også
          setCustomPromptInput(''); // Reset input state også
        }
      } catch (error) {
        console.error('Fejl ved hentning af AI-indstillinger:', error);
      }
    };

    fetchSettings();
  }, [selectedCustomer]); // Tilføj selectedCustomer som afhængighed så vi opdaterer når kunden ændres

  // Åbn modal for tilføjelse
  const openModal = (type) => {
    setModalType(type);
    // Nulstil inputfelter når modal åbnes
    if (type === 'profile') {
      setNewProfile('');
    } else if (type === 'product') {
      setNewProduct({ name: '', description: '' });
    } else if (type === 'leadMagnet') {
      setNewLeadMagnet({ name: '', description: '' });
    } else if (type === 'example') {
      setNewExample('');
    }
  };

  // Luk modal
  const closeModal = () => {
    setModalType(null);
  };

  // Tilføj LinkedIn-profil
  const addProfile = async (profile) => {
    if (profile.trim() === '') return;
    const updatedProfiles = [...linkedInProfiles, profile];
    setLinkedInProfiles(updatedProfiles);
    setNewProfile('');
    closeModal();
    await saveSettings(updatedProfiles, products, leadMagnets, examples);
  };

  // Fjern LinkedIn-profil
  const removeProfile = async (index) => {
    const updatedProfiles = [...linkedInProfiles];
    updatedProfiles.splice(index, 1);
    setLinkedInProfiles(updatedProfiles);
    await saveSettings(updatedProfiles, products, leadMagnets, examples);
  };

  // Tilføj produkt
  const addProduct = async (product) => {
    if (product.name.trim() === '') return;
    const updatedProducts = [...products, product];
    setProducts(updatedProducts);
    setNewProduct({ name: '', description: '' });
    closeModal();
    await saveSettings(linkedInProfiles, updatedProducts, leadMagnets, examples);
  };

  // Fjern produkt
  const removeProduct = async (index) => {
    const updatedProducts = [...products];
    updatedProducts.splice(index, 1);
    setProducts(updatedProducts);
    await saveSettings(linkedInProfiles, updatedProducts, leadMagnets, examples);
  };

  // Tilføj lead magnet
  const addLeadMagnet = async (magnet) => {
    if (magnet.name.trim() === '') return;
    const updatedLeadMagnets = [...leadMagnets, magnet];
    setLeadMagnets(updatedLeadMagnets);
    setNewLeadMagnet({ name: '', description: '' });
    closeModal();
    await saveSettings(linkedInProfiles, products, updatedLeadMagnets, examples);
  };

  // Fjern lead magnet
  const removeLeadMagnet = async (index) => {
    const updatedLeadMagnets = [...leadMagnets];
    updatedLeadMagnets.splice(index, 1);
    setLeadMagnets(updatedLeadMagnets);
    await saveSettings(linkedInProfiles, products, updatedLeadMagnets, examples);
  };

  // Tilføj eksempel
  const addExample = async (example) => {
    if (example.trim() === '') return;
    const updatedExamples = [...examples, example];
    setExamples(updatedExamples);
    setNewExample('');
    closeModal();
    await saveSettings(linkedInProfiles, products, leadMagnets, updatedExamples);
  };

  // Fjern eksempel
  const removeExample = async (index) => {
    const updatedExamples = [...examples];
    updatedExamples.splice(index, 1);
    setExamples(updatedExamples);
    await saveSettings(linkedInProfiles, products, leadMagnets, updatedExamples);
  };

  // Tilføj ny state for redigering
  const startEditing = (type, item, index) => {
    setEditingItem({ type, item, index });
    setModalType(`edit-${type}`);
    
    // Nulstil værdier når modal åbnes for redigering
    if (type === 'profile') {
      setNewProfile(item.profile);
    } else if (type === 'product') {
      setNewProduct(item);
    } else if (type === 'leadMagnet') {
      setNewLeadMagnet(item);
    } else if (type === 'example') {
      setNewExample(item.example);
    }
  };

  // Opdater produkt
  const updateProduct = async (updatedProduct, index) => {
    const updatedProducts = [...products];
    updatedProducts[index] = updatedProduct;
    setProducts(updatedProducts);
    closeModal();
    await saveSettings(linkedInProfiles, updatedProducts, leadMagnets, examples);
  };

  // Opdater LinkedIn profil
  const updateProfile = async (updatedProfile, index) => {
    const updatedProfiles = [...linkedInProfiles];
    updatedProfiles[index] = updatedProfile.profile;
    setLinkedInProfiles(updatedProfiles);
    closeModal();
    await saveSettings(updatedProfiles, products, leadMagnets, examples);
  };

  // Opdater lead magnet
  const updateLeadMagnet = async (updatedMagnet, index) => {
    const updatedMagnets = [...leadMagnets];
    updatedMagnets[index] = updatedMagnet;
    setLeadMagnets(updatedMagnets);
    closeModal();
    await saveSettings(linkedInProfiles, products, updatedMagnets, examples);
  };

  // Opdater eksempel
  const updateExample = async (updatedExample, index) => {
    const updatedExamples = [...examples];
    updatedExamples[index] = updatedExample.example;
    setExamples(updatedExamples);
    closeModal();
    await saveSettings(linkedInProfiles, products, leadMagnets, updatedExamples);
  };

  // Tilføj funktion til at opdatere foretrukne emojis
  const updatePreferredEmojis = async (newEmojis) => {
    setPreferredEmojis(newEmojis);
    
    // Gem straks efter ændring
    await saveSettings(linkedInProfiles, products, leadMagnets, examples);
  };

  // Tilføj funktion til at opdatere opslagssprog
  const updatePostLanguage = async (newLanguage) => {
    console.log(`DEBUGGING: Ændrer sprog fra ${postLanguage} til ${newLanguage}`);
    setPostLanguage(newLanguage);
    
    // Gem direkte i Firestore uden at vente på state-opdatering
    if (!auth.currentUser) {
      console.error("Ingen bruger logget ind");
      return;
    }

    try {
      // Brug getEffectiveUserId for at få den korrekte bruger-ID baseret på om vi arbejder på vegne af en kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        console.error("Ingen gyldig bruger-ID fundet");
        return;
      }
      
      console.log(`DEBUGGING: Gemmer nyt sprog direkte til Firestore: ${newLanguage} for bruger ${effectiveUserId}`);
      
      const settingsRef = doc(db, 'users', effectiveUserId, 'settings', 'ai_prompts');
      
      // Vi gemmer DIREKTE det nye sprog - ikke fra state
      await setDoc(settingsRef, {
        postLanguage: newLanguage,
        updatedAt: new Date()
      }, { merge: true });

      console.log(`DEBUGGING: Sprog opdateret til: ${newLanguage}`);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Fejl ved opdatering af sprog:', error);
      setSaveStatus('error');
    }
  };

  // Gem prompt-indstillinger
  const saveSettings = async (profiles, prods, magnets, examps) => {
    if (!auth.currentUser) {
      console.error("Ingen bruger logget ind");
      return;
    }

    setIsSaving(true);
    setSaveStatus('');
    
    try {
      // Brug getEffectiveUserId for at få den korrekte bruger-ID baseret på om vi arbejder på vegne af en kunde
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        console.error("Ingen gyldig bruger-ID fundet");
        return;
      }
      
      console.log("Gemmer indstillinger for bruger:", effectiveUserId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
      
      const settingsRef = doc(db, 'users', effectiveUserId, 'settings', 'ai_prompts');
      
      await setDoc(settingsRef, {
        linkedInProfiles: profiles,
        products: prods,
        leadMagnets: magnets,
        examples: examps,
        ctaFrequency: ctaFrequency, // Gem CTA-frekvensen
        emojiFrequency: emojiFrequency, // Gem emoji-frekvensen
        preferredEmojis: preferredEmojis, // Gem brugerens foretrukne emojis
        postLanguage: postLanguage, // Gem opslagssprog
        aboutMe: settings.aboutMe,  // Gem about-felt
        customPrompt: settings.customPrompt,  // Gem brugerdefineret prompt
        updatedAt: new Date()
      }, { merge: true });

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Fejl ved gemning af AI-indstillinger:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Tilføj funktion til at opdatere CTA-frekvens
  const updateCtaFrequency = async (newFrequency) => {
    setCtaFrequency(newFrequency);
    
    // Gem straks efter ændring
    await saveSettings(linkedInProfiles, products, leadMagnets, examples);
  };

  // Tilføj funktion til at opdatere emoji-frekvens
  const updateEmojiFrequency = async (newFrequency) => {
    setEmojiFrequency(newFrequency);
    
    // Gem straks efter ændring
    await saveSettings(linkedInProfiles, products, leadMagnets, examples);
  };

  // Tilføj funktion til at opdatere about-felt med debouncing
  const updateAboutMe = (newAboutMe) => {
    // Opdater lokalt state øjeblikkeligt (for responsivt input)
    setAboutMeInput(newAboutMe);
    
    // Clear eksisterende timeout
    if (aboutMeTimeoutRef.current) {
      clearTimeout(aboutMeTimeoutRef.current);
    }
    
    // Sæt ny timeout til at gemme efter 1 sekund
    aboutMeTimeoutRef.current = setTimeout(async () => {
      try {
        const currentUserId = getEffectiveUserId(selectedCustomer);
        const settingsRef = doc(collection(doc(db, 'users', currentUserId), 'settings'), 'ai_prompts');
        await updateDoc(settingsRef, {
          aboutMe: newAboutMe
        });
        
        // Opdater settings state efter gemt
        setSettings(prev => ({ ...prev, aboutMe: newAboutMe }));
        console.log('About Me gemt:', newAboutMe);
      } catch (error) {
        console.error('Fejl ved opdatering af "About me":', error);
      }
    }, 1000); // Gem efter 1 sekund uden ændringer
  };

  const updateCustomPrompt = (newCustomPrompt) => {
    // Opdater lokalt state øjeblikkeligt (for responsivt input)
    setCustomPromptInput(newCustomPrompt);
    
    // Clear eksisterende timeout
    if (customPromptTimeoutRef.current) {
      clearTimeout(customPromptTimeoutRef.current);
    }
    
    // Sæt ny timeout til at gemme efter 1 sekund
    customPromptTimeoutRef.current = setTimeout(async () => {
      try {
        const currentUserId = getEffectiveUserId(selectedCustomer);
        const settingsRef = doc(collection(doc(db, 'users', currentUserId), 'settings'), 'ai_prompts');
        await updateDoc(settingsRef, {
          customPrompt: newCustomPrompt
        });
        
        // Opdater settings state efter gemt
        setSettings(prev => ({ ...prev, customPrompt: newCustomPrompt }));
        console.log('Brugerdefineret prompt gemt:', newCustomPrompt);
      } catch (error) {
        console.error('Fejl ved opdatering af brugerdefineret prompt:', error);
      }
    }, 1000); // Gem efter 1 sekund uden ændringer
  };

  // Cleanup function for timeout
  useEffect(() => {
    return () => {
      if (aboutMeTimeoutRef.current) {
        clearTimeout(aboutMeTimeoutRef.current);
      }
      if (customPromptTimeoutRef.current) {
        clearTimeout(customPromptTimeoutRef.current);
      }
    };
  }, []);

  // Modal komponent
  const Modal = ({ title, onClose, onSubmit, type, initialValues = {} }) => {
    const [values, setValues] = useState(initialValues);
    const isEditing = type.startsWith('edit-');
    const baseType = isEditing ? type.replace('edit-', '') : type;
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(values);
    };

    return (
      <div className="ai-settings-modal-overlay">
        <div className="ai-settings-modal">
          <div className="ai-settings-modal-header">
            <h3>{title}</h3>
            <button className="ai-settings-modal-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="ai-settings-modal-content">
            <form className="ai-settings-modal-form" onSubmit={handleSubmit}>
              {(baseType === 'profile' || baseType === 'edit-profile') && (
                <input
                  type="text"
                  placeholder={t('aiPromptSettings', 'modal.placeholders.profile')}
                  value={values.profile || ''}
                  onChange={(e) => setValues({ profile: e.target.value })}
                  className="ai-settings-input"
                />
              )}
              
              {(baseType === 'product' || baseType === 'edit-product') && (
                <>
                  <input
                    type="text"
                    placeholder={t('aiPromptSettings', 'modal.placeholders.productName')}
                    value={values.name || ''}
                    onChange={(e) => setValues({ ...values, name: e.target.value })}
                    className="ai-settings-input"
                  />
                  <textarea
                    placeholder={t('aiPromptSettings', 'modal.placeholders.productDescription')}
                    value={values.description || ''}
                    onChange={(e) => setValues({ ...values, description: e.target.value })}
                    className="ai-settings-textarea"
                    rows={3}
                  />
                </>
              )}
              
              {(baseType === 'leadMagnet' || baseType === 'edit-leadMagnet') && (
                <>
                  <input
                    type="text"
                    placeholder={t('aiPromptSettings', 'modal.placeholders.leadMagnetName')}
                    value={values.name || ''}
                    onChange={(e) => setValues({ ...values, name: e.target.value })}
                    className="ai-settings-input"
                  />
                  <textarea
                    placeholder={t('aiPromptSettings', 'modal.placeholders.leadMagnetDescription')}
                    value={values.description || ''}
                    onChange={(e) => setValues({ ...values, description: e.target.value })}
                    className="ai-settings-textarea"
                    rows={3}
                  />
                </>
              )}
              
              {(baseType === 'example' || baseType === 'edit-example') && (
                <textarea
                  placeholder={t('aiPromptSettings', 'modal.placeholders.example')}
                  value={values.example || ''}
                  onChange={(e) => setValues({ example: e.target.value })}
                  className="ai-settings-textarea"
                  rows={6}
                />
              )}
              
              <div className="ai-settings-modal-actions">
                <button type="button" className="ai-settings-modal-cancel" onClick={onClose}>
                  {t('aiPromptSettings', 'modal.buttons.cancel')}
                </button>
                <button type="submit" className="ai-settings-modal-submit">
                  {isEditing ? t('aiPromptSettings', 'modal.buttons.save') : t('aiPromptSettings', 'modal.buttons.add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      
      <main className="dashboard-content">
        <div className="ai-settings-page-header">
          <h1 className="ai-settings-page-title">{t('aiPromptSettings', 'header.title')}</h1>
          <p className="ai-settings-page-description">{t('aiPromptSettings', 'header.description')}</p>
        </div>
        
        <div className={`ai-settings-container ${isMenuCollapsed ? 'menu-collapsed' : ''}`}>
          {/* Vis banner, hvis man arbejder på vegne af kunde */}
          {isWorkingOnBehalfOfCustomer && (
            <div className="customer-action-banner">
              <i className="fas fa-user-friends"></i>
              <span>{t('aiPromptSettings', 'customerBanner', { customerName: selectedCustomer.name || selectedCustomer.email })}</span>
            </div>
          )}
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-user-circle ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'about.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'about.helpText')}
              </p>
            </div>
            
            <div className="about-me-container">
              <label htmlFor="about-me" className="about-me-label">
                {t('aiPromptSettings', 'about.label')}
              </label>
              <div className="about-me-input-container">
                <textarea
                  id="about-me"
                  value={aboutMeInput}
                  onChange={(e) => updateAboutMe(e.target.value)}
                  placeholder={t('aiPromptSettings', 'about.placeholder')}
                  className="about-me-textarea"
                  rows={4}
                />
              </div>
            </div>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fab fa-linkedin ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.profiles.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.profiles.description')}
              </p>
            </div>
            
            <div className="ai-settings-item-grid">
              {linkedInProfiles.map((profile, index) => (
                <div key={index} className="ai-settings-item-card">
                  <div className="ai-settings-item-content">
                    <i className="fab fa-linkedin-in ai-settings-item-icon"></i>
                    <div className="ai-settings-item-text">
                      <a href={profile} target="_blank" rel="noopener noreferrer">{profile}</a>
                    </div>
                  </div>
                  <div className="ai-settings-item-actions">
                    <button 
                      className="ai-settings-edit-btn" 
                      onClick={() => startEditing('profile', { profile }, index)}
                      aria-label="Rediger profil"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  <button 
                      className="ai-settings-remove-btn" 
                    onClick={() => removeProfile(index)}
                    aria-label="Fjern profil"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              className="ai-settings-add-button"
              onClick={() => openModal('profile')}
            >
              <i className="fas fa-plus"></i> {t('aiPromptSettings', 'buttons.add.profile')}
            </button>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-box-open ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.products.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.products.description')}
              </p>
            </div>
            
            <div className="ai-settings-item-grid ai-settings-products-grid">
              {products.map((product, index) => (
                <div key={index} className="ai-settings-item-card ai-settings-product-card">
                  <div className="ai-settings-product-header">
                    <h3>{product.name}</h3>
                    <div className="ai-settings-item-actions">
                      <button 
                        className="ai-settings-edit-btn" 
                        onClick={() => startEditing('product', product, index)}
                        aria-label="Rediger produkt"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                  <button 
                        className="ai-settings-remove-btn" 
                    onClick={() => removeProduct(index)}
                    aria-label="Fjern produkt"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                    </div>
                  </div>
                  <p className="ai-settings-product-description">{product.description}</p>
                </div>
              ))}
            </div>
            
            <button 
              className="ai-settings-add-button"
              onClick={() => openModal('product')}
            >
              <i className="fas fa-plus"></i> {t('aiPromptSettings', 'buttons.add.product')}
            </button>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-gift ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.leadMagnets.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.leadMagnets.description')}
              </p>
            </div>
            
            <div className="ai-settings-item-grid">
              {leadMagnets.map((magnet, index) => (
                <div key={index} className="ai-settings-item-card ai-settings-magnet-card">
                  <div className="ai-settings-magnet-header">
                    <h3><i className="fas fa-gift"></i> {magnet.name}</h3>
                  </div>
                  <p className="ai-settings-magnet-description">{magnet.description}</p>
                  <div className="ai-settings-item-actions">
                    <button 
                      className="ai-settings-edit-btn" 
                      onClick={() => startEditing('leadMagnet', magnet, index)}
                      aria-label="Rediger lead magnet"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  <button 
                      className="ai-settings-remove-btn" 
                    onClick={() => removeLeadMagnet(index)}
                    aria-label="Fjern lead magnet"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              className="ai-settings-add-button"
              onClick={() => openModal('leadMagnet')}
            >
              <i className="fas fa-plus"></i> {t('aiPromptSettings', 'buttons.add.leadMagnet')}
            </button>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-star ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.examples.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.examples.description')}
              </p>
            </div>
            
            <div className="ai-settings-item-grid ai-settings-examples-grid">
              {examples.map((example, index) => (
                <div key={index} className="ai-settings-item-card ai-settings-example-card">
                  <div className="ai-settings-example-content">
                    <p className="ai-settings-example-text">{example}</p>
                  </div>
                  <div className="ai-settings-item-actions">
                    <button 
                      className="ai-settings-edit-btn" 
                      onClick={() => startEditing('example', { example }, index)}
                      aria-label="Rediger eksempel"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button 
                      className="ai-settings-remove-btn" 
                      onClick={() => removeExample(index)}
                      aria-label="Fjern eksempel"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              className="ai-settings-add-button"
              onClick={() => openModal('example')}
            >
              <i className="fas fa-plus"></i> {t('aiPromptSettings', 'buttons.add.example')}
            </button>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-bullhorn ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.callToAction.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.callToAction.description')}
              </p>
            </div>
            
            <div className="preferences-slider-container">
              <div className="preferences-slider-value">
                {ctaFrequency === 'always' ? t('aiPromptSettings', 'ctaFrequency.always') : 
                 ctaFrequency === 'every2' ? t('aiPromptSettings', 'ctaFrequency.every2') : 
                 ctaFrequency === 'every3' ? t('aiPromptSettings', 'ctaFrequency.every3') : 
                 ctaFrequency === 'every4' ? t('aiPromptSettings', 'ctaFrequency.every4') : 
                 t('aiPromptSettings', 'ctaFrequency.never')}
              </div>
              
              <input 
                type="range" 
                min="0" 
                max="4" 
                step="1" 
                value={
                  ctaFrequency === 'always' ? 0 : 
                  ctaFrequency === 'every2' ? 1 : 
                  ctaFrequency === 'every3' ? 2 : 
                  ctaFrequency === 'every4' ? 3 : 4
                } 
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  const frequencyMap = {
                    0: 'always',
                    1: 'every2',
                    2: 'every3',
                    3: 'every4',
                    4: 'never'
                  };
                  updateCtaFrequency(frequencyMap[value]);
                }}
                className="preferences-slider"
              />
              
              <div className="preferences-slider-ticks">
                <div className={`preferences-slider-tick ${ctaFrequency === 'always' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'ctaFrequency.always')}</span>
                </div>
                <div className={`preferences-slider-tick ${ctaFrequency === 'every2' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'ctaFrequency.every2')}</span>
                </div>
                <div className={`preferences-slider-tick ${ctaFrequency === 'every3' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'ctaFrequency.every3')}</span>
                </div>
                <div className={`preferences-slider-tick ${ctaFrequency === 'every4' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'ctaFrequency.every4')}</span>
                </div>
                <div className={`preferences-slider-tick ${ctaFrequency === 'never' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'ctaFrequency.never')}</span>
                </div>
              </div>
              
              <div className="preferences-preview">
                <div className="preferences-preview-items">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((postNum) => {
                    let hasCTA = false;
                    
                    if (ctaFrequency === 'always') {
                      hasCTA = true;
                    } else if (ctaFrequency === 'every2') {
                      hasCTA = postNum % 2 === 1;
                    } else if (ctaFrequency === 'every3') {
                      hasCTA = postNum % 3 === 1;
                    } else if (ctaFrequency === 'every4') {
                      hasCTA = postNum % 4 === 1;
                    } else {
                      hasCTA = false;
                    }
                    
                    return (
                      <div 
                        key={postNum} 
                        className={`preferences-preview-item ${hasCTA ? 'active' : ''}`}
                        title={hasCTA ? t('aiPromptSettings', 'ctaFrequency.withCta') : t('aiPromptSettings', 'ctaFrequency.withoutCta')}
                      >
                        {postNum}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="far fa-smile ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.emoji.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.emoji.description')}
              </p>
            </div>
            
            <div className="preferences-slider-container">
              <div className="preferences-slider-value">
                {emojiFrequency === 'none' ? t('aiPromptSettings', 'emojiFrequency.none') : 
                 emojiFrequency === 'few' ? t('aiPromptSettings', 'emojiFrequency.few') : 
                 emojiFrequency === 'medium' ? t('aiPromptSettings', 'emojiFrequency.medium') : 
                 t('aiPromptSettings', 'emojiFrequency.many')}
              </div>
              
              <input 
                type="range" 
                min="0" 
                max="3" 
                step="1" 
                value={
                  emojiFrequency === 'none' ? 0 : 
                  emojiFrequency === 'few' ? 1 : 
                  emojiFrequency === 'medium' ? 2 : 3
                } 
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  const frequencyMap = {
                    0: 'none',
                    1: 'few',
                    2: 'medium',
                    3: 'many'
                  };
                  updateEmojiFrequency(frequencyMap[value]);
                }}
                className="preferences-slider"
              />
              
              <div className="preferences-slider-ticks">
                <div className={`preferences-slider-tick ${emojiFrequency === 'none' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'emojiFrequency.none')}</span>
                </div>
                <div className={`preferences-slider-tick ${emojiFrequency === 'few' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'emojiFrequency.few')}</span>
                </div>
                <div className={`preferences-slider-tick ${emojiFrequency === 'medium' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'emojiFrequency.medium')}</span>
                </div>
                <div className={`preferences-slider-tick ${emojiFrequency === 'many' ? 'active' : ''}`}>
                  <span className="preferences-slider-label">{t('aiPromptSettings', 'emojiFrequency.many')}</span>
                </div>
              </div>
              
              <div className="preferences-preview">
                <div className="preferences-emoji-examples">
                  {emojiFrequency === 'none' && 
                    <div className="preferences-emoji-example">
                      {t('aiPromptSettings', 'emojiFrequency.examples.none')}
                    </div>
                  }
                  {emojiFrequency === 'few' && 
                    <div className="preferences-emoji-example">
                      {t('aiPromptSettings', 'emojiFrequency.examples.few')}
                    </div>
                  }
                  {emojiFrequency === 'medium' && 
                    <div className="preferences-emoji-example">
                      {t('aiPromptSettings', 'emojiFrequency.examples.medium')}
                    </div>
                  }
                  {emojiFrequency === 'many' && 
                    <div className="preferences-emoji-example">
                      {t('aiPromptSettings', 'emojiFrequency.examples.many')}
                    </div>
                  }
                </div>
              </div>
            </div>
            
            {/* Nyt input til foretrukne emojis */}
            <div className="preferred-emojis-container">
              <label htmlFor="preferred-emojis" className="preferred-emojis-label">
                {t('aiPromptSettings', 'preferredEmojis.label')}
              </label>
              <div className="preferred-emojis-input-container">
                <input
                  id="preferred-emojis"
                  type="text"
                  value={preferredEmojis}
                  onChange={(e) => updatePreferredEmojis(e.target.value)}
                  placeholder={t('aiPromptSettings', 'preferredEmojis.placeholder')}
                  className="preferred-emojis-input"
                />
              </div>
              <p className="preferred-emojis-description">
                {t('aiPromptSettings', 'preferredEmojis.description')}
              </p>
            </div>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-language ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'sections.language.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'sections.language.description')}
              </p>
            </div>
            
            <div className="language-selection-container">
              <select 
                className="language-dropdown"
                value={postLanguage}
                onChange={(e) => updatePostLanguage(e.target.value)}
              >
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
              
              <div className="language-preview">
                <div className="language-preview-header">{t('aiPromptSettings', 'postLanguage.previewHeader')}</div>
                <div className="language-preview-content">
                  {postLanguage === 'da' && t('aiPromptSettings', 'postLanguage.examples.da')}
                  {postLanguage === 'en' && t('aiPromptSettings', 'postLanguage.examples.en')}
                  {postLanguage === 'no' && t('aiPromptSettings', 'postLanguage.examples.no')}
                  {postLanguage === 'sv' && t('aiPromptSettings', 'postLanguage.examples.sv')}
                </div>
              </div>
            </div>
          </div>
          
          <div className="ai-settings-section">
            <div className="ai-settings-section-header">
              <h2 className="ai-settings-section-title">
                <i className="fas fa-code ai-settings-section-icon"></i> 
                {t('aiPromptSettings', 'customPrompt.title')}
              </h2>
              <p className="ai-settings-section-desc">
                {t('aiPromptSettings', 'customPrompt.description')}
              </p>
            </div>
            
            <div className="custom-prompt-container">
              <label htmlFor="custom-prompt" className="custom-prompt-label">
                {t('aiPromptSettings', 'customPrompt.label')}
              </label>
              <div className="custom-prompt-input-container">
                <textarea
                  id="custom-prompt"
                  value={customPromptInput}
                  onChange={(e) => updateCustomPrompt(e.target.value)}
                  placeholder={t('aiPromptSettings', 'customPrompt.placeholder')}
                  className="custom-prompt-textarea"
                  rows={6}
                />
              </div>
              <p className="custom-prompt-description">
                {t('aiPromptSettings', 'customPrompt.warning')}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {modalType && (
        <Modal
          title={
            modalType === 'profile' ? t('aiPromptSettings', 'modal.titles.addProfile') :
            modalType === 'edit-profile' ? t('aiPromptSettings', 'modal.titles.editProfile') :
            modalType === 'product' ? t('aiPromptSettings', 'modal.titles.addProduct') :
            modalType === 'edit-product' ? t('aiPromptSettings', 'modal.titles.editProduct') :
            modalType === 'leadMagnet' ? t('aiPromptSettings', 'modal.titles.addLeadMagnet') :
            modalType === 'edit-leadMagnet' ? t('aiPromptSettings', 'modal.titles.editLeadMagnet') :
            modalType === 'example' ? t('aiPromptSettings', 'modal.titles.addExample') :
            t('aiPromptSettings', 'modal.titles.editExample')
          }
          type={modalType}
          onClose={closeModal}
          initialValues={editingItem?.item || {}}
          onSubmit={(values) => {
            if (modalType === 'profile') {
              addProfile(values.profile);
            } else if (modalType === 'edit-profile') {
              updateProfile(values, editingItem.index);
            } else if (modalType === 'product') {
              addProduct(values);
            } else if (modalType === 'edit-product') {
              updateProduct(values, editingItem.index);
            } else if (modalType === 'leadMagnet') {
              addLeadMagnet(values);
            } else if (modalType === 'edit-leadMagnet') {
              updateLeadMagnet(values, editingItem.index);
            } else if (modalType === 'example') {
              addExample(values.example);
            } else if (modalType === 'edit-example') {
              updateExample(values, editingItem.index);
            }
          }}
        />
      )}

      {saveStatus === 'success' && (
        <span className="ai-settings-status success ai-settings-floating-status">
          <i className="fas fa-check-circle"></i>
          {t('aiPromptSettings', 'save.success')}
        </span>
      )}
      
      {saveStatus === 'error' && (
        <span className="ai-settings-status error ai-settings-floating-status">
          <i className="fas fa-times-circle"></i>
          {t('aiPromptSettings', 'save.error')}
        </span>
      )}
    </div>
  );
};

export default AIPromptSettings; 